
import React, { useState } from 'react';
import { message } from 'antd';

/**
 * 可复用的用户详细信息获取组件
 * @param {Object} props
 * @param {string} props.apiBaseUrl - API基础URL
 * @param {function} props.onUserDetailsFetched - 用户详细信息获取完成后的回调函数
 * @param {function} props.onError - 错误回调函数
 */
export const StaffSearchComponent = ({
  apiBaseUrl = '',
  onUserDetailsFetched,
  onError
}) => {
  const [loading, setLoading] = useState(false);

  /**
   * 获取用户详细信息
   * @param {Array} userIds - 用户ID数组
   * @returns {Promise<Array>} 用户详细信息数组
   */
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) return [];

    setLoading(true);
    const userDetails = [];
    
    for (const userId of userIds) {
      try {
        const response = await fetch(`${apiBaseUrl}/dingtalk/user/detail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userid: userId,
            language: 'zh_CN'
          })
        });
        
        const result = await response.json();
        console.log("获取用户详情结果:", result);
        
        if (result.success) {
          userDetails.push({
            userId: userId,
            name: result.data.result.name,
            avatar: result.data.result.avatar,
            title: result.data.result.title
          });
        } else {
          const errorMsg = `获取用户${userId}详情失败: ${result.error || '未知错误'}`;
          console.error(errorMsg);
          if (onError) onError(errorMsg);
        }
      } catch (error) {
        const errorMsg = `获取用户${userId}详情异常: ${error.message}`;
        console.error(errorMsg);
        if (onError) onError(errorMsg);
      }
    }
    
    setLoading(false);
    
    // 调用回调函数
    if (onUserDetailsFetched) {
      onUserDetailsFetched(userDetails);
    }
    
    return userDetails;
  };

  /**
   * 搜索钉钉用户
   * @param {string} queryWord - 搜索关键词
   * @param {Object} options - 搜索选项
   * @param {number} options.offset - 偏移量，默认0
   * @param {number} options.size - 返回数量，默认10
   * @returns {Promise<Array>} 用户详细信息数组
   */
  const searchUsers = async (queryWord, options = {}) => {
    if (!queryWord || !queryWord.trim()) {
      const errorMsg = '请输入搜索关键词';
      message.warning(errorMsg);
      if (onError) onError(errorMsg);
      return [];
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${apiBaseUrl}/dingtalk/user/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryWord: queryWord.trim(),
          offset: options.offset || 0,
          size: options.size || 10,
        })
      });
      
      const result = await response.json();
      console.log("搜索用户结果:", result);
      
      if (result.success) {
        const userIds = result.data?.list || [];
        
        if (userIds.length === 0) {
          message.info('未找到匹配的用户');
          if (onUserDetailsFetched) onUserDetailsFetched([]);
          return [];
        } else {
          // 根据userid获取用户详细信息
          const userDetails = await fetchUserDetails(userIds);
          
          if (userDetails.length === 0) {
            const errorMsg = '获取用户详情失败';
            message.warning(errorMsg);
            if (onError) onError(errorMsg);
          }
          
          return userDetails;
        }
      } else {
        const errorMsg = `搜索失败: ${result.error || '未知错误'}`;
        message.error(errorMsg);
        if (onError) onError(errorMsg);
        return [];
      }
    } catch (error) {
      const errorMsg = `搜索用户错误: ${error.message}`;
      console.error(errorMsg);
      message.error('搜索失败，请稍后重试');
      if (onError) onError(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    fetchUserDetails,
    searchUsers
  };
};

export default StaffSearchComponent;
